import React, { useEffect, useState, useMemo } from "react";
import {
  Modal,
  Button,
  Input,
  Table,
  Row,
  Col,
  message,
  Form,
  Divider,
  Card,
  Statistic,
} from "antd";
import dayjs from "dayjs";
import { Method_POST_CONTEXTO } from "../../../../service/api";
import { NumericFormat } from "react-number-format";
import { useDraggableModal } from "../Balancete/utils/modalDraggable";

/* =============================
 *  UTILITÁRIOS
 * ============================= */
const formatCurrency = (v = 0) =>
  Number(v).toLocaleString("pt-BR", {
    style: "currency",
    currency: "BRL",
  });

export const formatDateContabil = (d) =>
  dayjs(d).hour(12).minute(0).second(0).millisecond(0).toISOString();

export default function InformeRendimentosModal({
  open,
  onClose,
  onUpdate,
  pendenciaFiscal,
  title,
  zIndex,
  onFocus,
}) {
  const draggableProps = useDraggableModal(zIndex, onFocus, {
    open,
    modalKey: 'informe',
    initialWidth: 1200,
    initialHeight: 'auto',
    overflow: 'hidden'
  });
  /* =============================
   *  ESTADOS E CONTEXTO
   * ============================= */
  // Protege a leitura do sessionStorage contra quebras
  const company = useMemo(() => {
    try {
      return JSON.parse(sessionStorage.getItem("COMPANY")) || {};
    } catch {
      return {};
    }
  }, []);

  const clienteId = company?.id || 110;
  const clienteCnpj = company?.cnpj;
  const clienteNome = company?.nome;

  // Ano Base e Exercício
  const anoReferencia = dayjs().year() - 1;
  const anoExercicio = anoReferencia + 1;

  const dataInicio = dayjs(`${anoReferencia}-01-01`);
  const dataTermino = dayjs(`${anoReferencia}-12-31`);

  const [aliquota, setAliquota] = useState(0);
  const [valorFaturamento, setValorFaturamento] = useState(0);
  const [valorDespesas, setValorDespesas] = useState(0);
  const [lucrosAcumulados, setLucrosAcumulados] = useState(0);
  const [socios, setSocios] = useState([]);
  const [editSocioOpen, setEditSocioOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [showModal, setShowModal] = useState(false);

  /* =============================
   *  CÁLCULOS MEMOIZADOS (Performance)
   * ============================= */
  const resultadoBruto = useMemo(
    () => valorFaturamento - valorDespesas,
    [valorFaturamento, valorDespesas]
  );

  const totalDisponivel = useMemo(
    () => resultadoBruto + lucrosAcumulados,
    [resultadoBruto, lucrosAcumulados]
  );

  const valorDistribuicao = useMemo(
    () => totalDisponivel > 0 ? (aliquota / 100) * totalDisponivel : 0,
    [aliquota, totalDisponivel]
  );

  const valorReserva = useMemo(
    () => totalDisponivel - valorDistribuicao,
    [totalDisponivel, valorDistribuicao]
  );

  const somaPercentualSocios = useMemo(() => {
    return socios.reduce((acc, s) => {
      const valor = Number(s.recurso?.percentualSocietario || 0);
      return acc + Math.round(valor * 100);
    }, 0) / 100;
  }, [socios]);

  // Mapeamento EXATO das colunas da tabela 'informe_rendimentos'
  const buildPayload = (listaSocios = socios) => ({
    periodo: {
      dataInicio: formatDateContabil(dataInicio),
      dataTermino: formatDateContabil(dataTermino),
      cliente: { id: clienteId },
    },
    informes: listaSocios.map((obj) => ({
      // 1. Dados da Empresa (Exigido nas linhas 67-68 do Java)
      empresa: {
        id: clienteId,
        cnpj: clienteCnpj,
        razaoSocial: clienteNome // Mapeia para fldNomeEmpresarial
      },

      // 2. Dados do Sócio (Exigido nas linhas 70-71 do Java)
      recurso: {
        id: obj.recurso.id,
        cpf: obj.recurso.cpf,
        nome: obj.recurso.nome, // Mapeia para fldNomeCompleto
        percentualSocietario: obj.recurso.percentualSocietario,
      },

      // 3. Dados do Responsável (Exigido nas linhas 94-96 do Java)
      responsavel: {
        nome: "", // O Java usará este nome para o campo fldResponsavelNome
        data: dayjs().valueOf() // Envia a data atual (o Java espera um objeto Date/Long)
      },

      ano_referencia: anoReferencia,
      ano_exercicio: anoExercicio,
      dados_complementares: `Percentual societário base: ${obj.recurso.percentualSocietario}%`,

      // 4. Lista de Itens (Exigido na linha 77 do Java)
      // O Java vai remover os pontos dos códigos. Ex: "3.1" vira "31" e preenche "fld31"
      itens: [
        { codigo: "3.1", valor: obj.valorProLabore || 0 },
        { codigo: "3.2", valor: obj.valorINSS || 0 },
        { codigo: "3.5", valor: obj.valorIRPF || 0 },
        {
          codigo: (company?.regimeTributario?.id === "LP" || company?.regimeTributario?.id === "LR")
            ? "4.5"
            : "4.6",
          valor: obj.valorParticipacaoResultado || 0
        },
        // Adicione outros códigos conforme as tags 'fld' do seu template-ir.pdf
      ]
    })),
  });

  /* =============================
   *  EFEITOS E BUSCA DE DADOS (GET)
   * ============================= */
  useEffect(() => {
    if (open) {
      setShowModal(false);
      const fetchInitialData = async () => {
        try {
          setLoading(true);

          // 1. Inicializar
          const resInit = await Method_POST_CONTEXTO("/cliente/empresa/distribuicao-resultado/inicializar", {
            ano: anoReferencia,
            tipo: { id: "RA" },
          });

          const faturamento = resInit?.valorFaturamento || 0;
          const despesas = resInit?.valorDebito || 0;
          const acum = resInit?.lucrosAcumulados || 0; // Tenta pegar do backend se existir
          const disponivel = (faturamento - despesas) + acum;
          const isPrejuizoTotal = disponivel <= 0;

          setAliquota(resInit?.aliquota || 0);
          setValorFaturamento(faturamento);
          setValorDespesas(despesas);
          setLucrosAcumulados(acum);

          // 2. Carregar Sócios
          const resSocio = await Method_POST_CONTEXTO("/cliente/empresa/distribuicao-resultado/socio/listar", {
            ano: anoReferencia,
            recurso: { situacao: { id: "A" } },
          });

          const sociosComValores = (resSocio || []).map((s) => ({
            ...s,
            valorParticipacaoResultado: isPrejuizoTotal ? 0 : (s.valorParticipacaoResultado ?? 0),
            valorProLabore: s.valorProLabore ?? 0,
            valorINSS: s.valorINSS ?? 0,
            valorIRPF: s.valorIRPF ?? 0,
            manual: false,
          }));

          setSocios(sociosComValores);

          // 3. Verificações de mensagens
          if (isPrejuizoTotal) {
            if (company?.prolabore) {
              message.warning("A empresa apresentou prejuízo total (com lucros acumulados), mas possui Pró-labore. O lucro distribuído foi zerado inicialmente, ajuste se necessário.");
            } else {
              message.info("Resultado total zerado ou negativo. Caso possua lucros acumulados de anos anteriores, informe-os manualmente no campo 'Lucros Acumulados'.");
            }
          }

          setShowModal(true);
          setLoading(false);

        } catch (error) {
          message.error("Erro ao carregar dados iniciais.");
          setLoading(false);
          onClose(); // Fallback caso quebre
        }
      };

      fetchInitialData();
    } else {
      setShowModal(false);
    }
  }, [open]);

  // Recalcula distribuição se os valores globais mudarem (respeitando quem foi editado manualmente)
  useEffect(() => {
    if (!socios.length) return;

    setSocios((prev) =>
      prev.map((s) => {
        if (s.manual) return s;
        return {
          ...s,
          valorParticipacaoResultado: (s.recurso.percentualSocietario * valorDistribuicao) / 100,
        };
      })
    );
  }, [valorDistribuicao]);
  /* =============================
   *  MANIPULAÇÃO DA TABELA
   * ============================= */
  const handleSocioChange = (index, field, value) => {
    setSocios((prev) => {
      const copy = [...prev];
      copy[index] = {
        ...copy[index],
        [field]: value,
        manual: field === "valorParticipacaoResultado", // Trava o recálculo automático apenas se mexer na participação
      };
      return copy;
    });
  };

  const handleSocioPercentualChange = (index, value) => {
    const percentual = Math.min(value, 100);

    setSocios((prev) => {
      const copy = [...prev];
      // Ao mudar o percentual, atualiza imediatamente a participação na tela
      const novaParticipacao = copy[index].manual
        ? copy[index].valorParticipacaoResultado
        : (percentual * valorDistribuicao) / 100;

      copy[index] = {
        ...copy[index],
        valorParticipacaoResultado: novaParticipacao,
        recurso: {
          ...copy[index].recurso,
          percentualSocietario: percentual,
        },
      };
      return copy;
    });
  };

  const columns = useMemo(() => [
    {
      title: "CPF - Nome",
      render: (_, r) => `${r.recurso.cpf} - ${r.recurso.nome}`,
    },
    {
      title: "Percentual",
      align: "right",
      render: (_, r, i) =>
        editSocioOpen ? (
          <NumericFormat
            customInput={Input}
            decimalScale={2}
            fixedDecimalScale
            suffix="%"
            allowNegative={false}
            isAllowed={(values) => (values.floatValue === undefined || values.floatValue <= 100)}
            value={r.recurso.percentualSocietario}
            onValueChange={(v) => handleSocioPercentualChange(i, v.floatValue || 0)}
          />
        ) : (
          `${Number(r.recurso.percentualSocietario).toFixed(2)} %`
        ),
    },
    {
      title: "Participação",
      align: "right",
      render: (_, r, i) =>
        editSocioOpen ? (
          <NumericFormat
            customInput={Input}
            thousandSeparator="."
            decimalSeparator=","
            decimalScale={2}
            fixedDecimalScale
            prefix="R$ "
            value={r.valorParticipacaoResultado}
            onValueChange={(v) => handleSocioChange(i, "valorParticipacaoResultado", v.floatValue || 0)}
          />
        ) : (
          formatCurrency(r.valorParticipacaoResultado)
        ),
    },
    {
      title: "Pró-labore",
      align: "right",
      render: (_, r, i) =>
        editSocioOpen ? (
          <NumericFormat
            customInput={Input}
            thousandSeparator="."
            decimalSeparator=","
            decimalScale={2}
            fixedDecimalScale
            prefix="R$ "
            value={r.valorProLabore}
            onValueChange={(v) => handleSocioChange(i, "valorProLabore", v.floatValue || 0)}
          />
        ) : (
          formatCurrency(r.valorProLabore)
        ),
    },
    {
      title: "INSS",
      align: "right",
      render: (_, r, i) =>
        editSocioOpen ? (
          <NumericFormat
            customInput={Input}
            thousandSeparator="."
            decimalSeparator=","
            decimalScale={2}
            fixedDecimalScale
            prefix="R$ "
            value={r.valorINSS}
            onValueChange={(v) => handleSocioChange(i, "valorINSS", v.floatValue || 0)}
          />
        ) : (
          formatCurrency(r.valorINSS)
        ),
    },
    {
      title: "IRPF",
      align: "right",
      render: (_, r, i) =>
        editSocioOpen ? (
          <NumericFormat
            customInput={Input}
            thousandSeparator="."
            decimalSeparator=","
            decimalScale={2}
            fixedDecimalScale
            prefix="R$ "
            value={r.valorIRPF}
            onValueChange={(v) => handleSocioChange(i, "valorIRPF", v.floatValue || 0)}
          />
        ) : (
          formatCurrency(r.valorIRPF)
        ),
    },
  ], [editSocioOpen, socios, valorDistribuicao]);

  /* =============================
   *  VALIDAÇÃO E PAYLOAD (Banco de Dados)
   * ============================= */
  const validarAntesSalvar = () => {
    if (Math.round(somaPercentualSocios * 100) !== 10000) {
      message.error("A soma dos percentuais dos sócios deve ser exatamente 100%");
      return false;
    }
    if (valorDistribuicao > totalDisponivel) {
      message.error("O valor distribuído não pode ser maior que o total disponível (Resultado + Lucros Acumulados)");
      return false;
    }
    if (totalDisponivel < 0 && !company?.prolabore) {
      message.error("Resultado total negativo. Verifique os valores informados");
      return false;
    }
    return true;
  };

  /* =============================
   *  API (POST)
   * ============================= */
  const salvarInforme = async () => {
    try {
      setLoading(true);
      await Method_POST_CONTEXTO("/empresa/contabilidade/prepare/defis/job/informe", buildPayload());
      message.success("Informe salvo com sucesso!");
      return true;
    } catch (err) {
      message.error("Erro ao salvar o informe");
      return false;
    } finally {
      setLoading(false);
    }
  };

  const gerarDefis = async () => {
    try {
      setLoading(true);
      const response = await Method_POST_CONTEXTO(
        `/empresa/contabilidade/prepare/defis/job?pendencia=${pendenciaFiscal}`,
        buildPayload()
      );

      if (response?.erro) {
        message.error(response.erro.mensagem || "Erro ao gerar DEFIS");
        return false;
      }
      message.success("DEFIS gerada com sucesso!");
      return true;
    } catch (err) {
      message.error("Erro ao gerar DEFIS");
      return false;
    } finally {
      setLoading(false);
    }
  };

  /* =============================
   *  HANDLERS DOS BOTÕES
   * ============================= */
  const handleSalvarInforme = async () => {
    if (!validarAntesSalvar()) return;
    const ok = await salvarInforme();
    if (ok) {
      if (onUpdate) onUpdate();
      onClose();
    }
  };

  const handleSalvarInformeEdefis = async () => {
    if (!validarAntesSalvar()) return;
    const okInforme = await salvarInforme();
    if (!okInforme) return;

    const okDefis = await gerarDefis();
    if (okDefis) {
      if (onUpdate) onUpdate();
      onClose();
    }
  };

  /* =============================
   *  RENDER
   * ============================= */
  return (
    <Modal
      open={open && showModal}
      onCancel={onClose}
      width={1200}
      title={title || "Informe de Rendimentos – Distribuição de Resultado"}
      {...draggableProps}
      styles={{
        body: {
          maxHeight: "80vh",
          padding: '24px',
          overflowY: "auto"
        }
      }}
      footer={[
        <Button key="edit" onClick={() => setEditSocioOpen(!editSocioOpen)}>
          {editSocioOpen ? "Cancelar edição" : "Editar sócios"}
        </Button>,

        <Button
          key="informe"
          onClick={handleSalvarInforme}
          loading={loading}
        >
          Salvar Informe
        </Button>,

        <Button
          key="defis"
          type="primary"
          onClick={handleSalvarInformeEdefis}
          loading={loading}
        >
          Salvar + Gerar DEFIS
        </Button>,
      ]}
    >
      <Form layout="vertical">
        <Row gutter={16}>
          <Col span={6}>
            <Form.Item label="Faturamento Bruto"
              tooltip="Valor total faturado antes das deduções."
            >
              <NumericFormat
                customInput={Input}
                thousandSeparator="."
                decimalSeparator=","
                decimalScale={2}
                fixedDecimalScale
                prefix="R$ "
                value={valorFaturamento}
                onValueChange={(v) =>
                  setValorFaturamento(v.floatValue || 0)
                }
              />
            </Form.Item>
          </Col>

          <Col span={6}>
            <Form.Item
              label="Impostos e Despesas"
              tooltip="Total de gastos e tributos."
            >
              <NumericFormat
                customInput={Input}
                thousandSeparator="."
                decimalSeparator=","
                decimalScale={2}
                fixedDecimalScale
                prefix="R$ "
                value={valorDespesas}
                onValueChange={(v) =>
                  setValorDespesas(v.floatValue || 0)
                }
              />
            </Form.Item>
          </Col>
          <Col span={6}>
            <Form.Item
              label="Lucros Acumulados"
              tooltip="Lucros de exercícios anteriores disponíveis para distribuição."
            >
              <NumericFormat
                customInput={Input}
                thousandSeparator="."
                decimalSeparator=","
                decimalScale={2}
                fixedDecimalScale
                prefix="R$ "
                value={lucrosAcumulados}
                onValueChange={(v) =>
                  setLucrosAcumulados(v.floatValue || 0)
                }
              />
            </Form.Item>
          </Col>
        </Row>
      </Form>

      <Divider />

      <Row gutter={16}>
        <Col span={6}>
          <strong>Resultado Bruto:</strong> {formatCurrency(resultadoBruto)}
        </Col>
        <Col span={6}>
          <strong>Total Disponível:</strong> {formatCurrency(totalDisponivel)}
        </Col>
        <Col span={6}>
          <strong>Distribuição:</strong> {formatCurrency(valorDistribuicao)}
        </Col>
        <Col span={6}>
          <strong>Reserva:</strong> {formatCurrency(valorReserva)}
        </Col>
      </Row>

      <Table
        rowKey={(r) => r.recurso.id}
        dataSource={socios}
        columns={columns}
        pagination={false}
      />
    </Modal>
  );
}